import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Video, CheckCircle2, XCircle, Hourglass, ExternalLink, PhoneCall } from 'lucide-react';

const when = (iso) => (iso ? new Date(iso).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

export const STATUS = {
  pending: { label: 'Waiting for a teacher', cls: 'bg-amber-100 text-amber-800', Icon: Hourglass },
  in_review: { label: 'Teacher is reviewing', cls: 'bg-blue-100 text-blue-800', Icon: Clock },
  delivered: { label: 'Delivered', cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
  declined: { label: 'Declined · refunded', cls: 'bg-rose-100 text-rose-800', Icon: XCircle },
};

export function StatusPill({ status }) {
  const s = STATUS[status] || { label: status, cls: 'bg-slate-100 text-slate-700', Icon: Clock };
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${s.cls}`} data-testid={`status-${status}`}><s.Icon size={11} /> {s.label}</span>;
}

/** The student's feedback requests, newest first. */
export function RequestList({ items, bookings, onRequest, onCalls }) {
  const calls = (bookings || []).filter((b) => b.status === 'booked');
  if (!items.length && !calls.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center" data-testid="requests-empty">
        <p className="font-semibold text-slate-800">No feedback requests yet</p>
        <p className="text-sm text-slate-500 mt-1">Pick any completed mock test and a teacher will go over every word of your answer in a personal video.</p>
        {onRequest ? <button type="button" onClick={onRequest} className="mt-4 inline-flex rounded-full bg-slate-900 text-white text-sm font-semibold px-5 py-2" data-testid="requests-empty-cta">Request on a mock test</button>
          : <Link to="/exams" className="mt-4 inline-flex rounded-full bg-slate-900 text-white text-sm font-semibold px-5 py-2" data-testid="requests-empty-cta">Go to Mock Exams</Link>}
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="requests-list">
      {calls.map((b) => (
        <div key={b.id} className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-4 flex items-center gap-3 flex-wrap" data-testid={`booking-${b.id}`}>
          <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0"><PhoneCall size={18} /></div>
          <div className="flex-1 min-w-[180px]">
            <p className="font-semibold text-slate-900 text-sm">1-1 call with {b.teacher_name || 'your teacher'}</p>
            <p className="text-xs text-slate-600">{when(b.start_at)} · {b.duration_minutes} min · {b.cost} credits</p>
          </div>
          {onCalls ? <button type="button" onClick={onCalls} className="text-xs font-semibold text-indigo-700 hover:underline" data-testid={`booking-open-${b.id}`}>Manage →</button>
            : <Link to="/feedback-credits?tab=calls" className="text-xs font-semibold text-indigo-700 hover:underline" data-testid={`booking-open-${b.id}`}>Manage →</Link>}
        </div>
      ))}
      {items.map((r) => (
        <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5" data-testid={`request-${r.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 text-sm md:text-base">{r.scope_label} review · <span className="text-slate-600 font-medium">{r.exam_name}</span></p>
              <p className="text-xs text-slate-500 mt-0.5">Requested {when(r.created_at)} · {r.cost} credits{r.teacher_name ? ` · ${r.teacher_name}` : ''}</p>
            </div>
            <StatusPill status={r.status} />
          </div>
          {r.student_note && <p className="mt-2 text-xs text-slate-500 italic">You wrote: “{r.student_note}”</p>}
          {r.status === 'delivered' && (
            <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 p-3 space-y-2" data-testid={`delivered-${r.id}`}>
              {r.loom_url && <a href={r.loom_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-700 text-white text-xs font-bold px-4 py-2 hover:bg-emerald-800" data-testid={`loom-${r.id}`}><Video size={13} /> Watch your teacher's video <ExternalLink size={11} /></a>}
              {r.note && <p className="text-sm text-slate-800 whitespace-pre-wrap">{r.note}</p>}
              <p className="text-[11px] text-slate-500">Delivered {when(r.delivered_at)} by {r.teacher_name}</p>
            </div>
          )}
          {r.status === 'declined' && <p className="mt-2 text-xs text-rose-700">{r.decline_reason ? `“${r.decline_reason}” — ` : 'The teacher could not review this one — '}your {r.cost} credits were refunded.</p>}
          <div className="mt-3"><Link to={r.results_url} className="text-xs font-semibold text-blue-700 hover:underline" data-testid={`request-results-${r.id}`}>Open the test results →</Link></div>
        </div>
      ))}
    </div>
  );
}
